/**
 * RequestTracesPanel — admin view of per-turn agent-pipeline traces. Each row is one user prompt; expanding
 * it shows HOW the answer was produced: which agents replied, how knowledge was retrieved (keyword Phase-1 vs
 * vector Phase-2, which scopes, how many chunks), the model tier, external integrations, the reply, and any
 * error along the ordered span timeline. Read-only; mirrors the Errors panel list+detail pattern.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, Search, Database, Cpu, Globe, Image, Wallet, AlertTriangle, MessageSquare } from 'lucide-react';
import { getRequestTraces, getRequestTrace, type TraceRow, type TraceDetail, type TraceSpan } from '../../services/adminService';

const card = 'rounded-2xl border border-secondary-200 dark:border-secondary-800 p-5';
const input = 'rounded-lg border border-secondary-300 dark:border-secondary-700 bg-white dark:bg-secondary-900 px-3 py-2 text-sm focus:outline-none focus:border-secondary-500';

const Chip: React.FC<{ active: boolean; onClick: () => void; children: React.ReactNode }> = ({ active, onClick, children }) => (
  <button onClick={onClick} className={`px-2.5 py-1 rounded-lg text-xs font-medium ${active ? 'bg-secondary-900 text-white dark:bg-white dark:text-secondary-900' : 'text-secondary-500 hover:bg-secondary-100 dark:hover:bg-secondary-800'}`}>{children}</button>
);

const statusColor = (s: string) => s === 'error' ? 'text-red-600 dark:text-red-400' : s === 'partial' ? 'text-amber-600 dark:text-amber-400' : 'text-secondary-500';
const modeLabel = (m: string) => m === 'hybrid' ? 'Phase 2 (vector+keyword)' : m === 'keyword' ? 'Phase 1 (keyword)' : 'none';
const spanIcon = (t: string) => {
  const c = 'w-3.5 h-3.5';
  if (t === 'retrieval') return <Database className={c} />;
  if (t === 'reasoning') return <Cpu className={c} />;
  if (t === 'web') return <Globe className={c} />;
  if (t === 'media') return <Image className={c} />;
  if (t === 'wallet') return <Wallet className={c} />;
  if (t === 'error') return <AlertTriangle className={c} />;
  return <MessageSquare className={c} />;
};

const SpanRow: React.FC<{ s: TraceSpan }> = ({ s }) => (
  <div className={`flex items-start gap-2 py-1.5 border-t border-secondary-100 dark:border-secondary-800 ${s.status === 'error' ? 'text-red-600 dark:text-red-400' : ''}`}>
    <span className="mt-0.5 text-secondary-400">{spanIcon(s.type)}</span>
    <div className="min-w-0 flex-1">
      <div className="flex items-center gap-2 text-xs">
        <span className="font-medium">{s.step}</span>
        <span className="text-secondary-400">+{s.at_ms}ms{typeof s.duration_ms === 'number' ? ` · ${s.duration_ms}ms` : ''}</span>
      </div>
      {s.detail && <pre className="mt-0.5 whitespace-pre-wrap break-words text-[11px] text-secondary-500 font-mono">{JSON.stringify(s.detail)}</pre>}
      {s.error && <div className="mt-0.5 text-[11px] font-mono">{s.error.message}</div>}
    </div>
  </div>
);

const TraceDetailView: React.FC<{ id: string }> = ({ id }) => {
  const [d, setD] = useState<TraceDetail | null>(null);
  const [err, setErr] = useState('');
  useEffect(() => {
    let alive = true;
    getRequestTrace(id).then((t) => { if (alive) setD(t); }).catch((e) => { if (alive) setErr(e?.message || 'Failed'); });
    return () => { alive = false; };
  }, [id]);
  if (err) return <div className="p-4 text-xs text-red-600">{err}</div>;
  if (!d) return <div className="p-4 flex items-center gap-2 text-xs text-secondary-500"><Loader2 className="w-4 h-4 animate-spin" /> Loading trace…</div>;
  return (
    <div className="p-4 space-y-3 bg-secondary-50 dark:bg-secondary-900/40">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
        <div><div className="text-secondary-400">Retrieval</div><div className="font-medium">{modeLabel(d.retrieval_mode)}</div></div>
        <div><div className="text-secondary-400">Chunks</div><div className="font-medium">{d.retrieval_chunks}</div></div>
        <div><div className="text-secondary-400">Model tiers</div><div className="font-medium">{d.model_tiers.join(', ') || '—'}</div></div>
        <div><div className="text-secondary-400">Duration</div><div className="font-medium">{d.duration_ms}ms</div></div>
      </div>
      {!!d.retrieval_scopes.length && (
        <div className="text-xs"><div className="text-secondary-400 mb-1">KB scopes read</div>
          <div className="flex flex-wrap gap-1">{d.retrieval_scopes.map((s) => <span key={s} className="px-1.5 py-0.5 rounded bg-secondary-100 dark:bg-secondary-800 font-mono text-[11px]">{s}</span>)}</div></div>
      )}
      {!!d.integrations.length && (
        <div className="text-xs"><div className="text-secondary-400 mb-1">Integrations</div>
          <div className="flex flex-wrap gap-1">{d.integrations.map((s) => <span key={s} className="px-1.5 py-0.5 rounded bg-secondary-100 dark:bg-secondary-800 font-mono text-[11px]">{s}</span>)}</div></div>
      )}
      <div className="text-xs"><div className="text-secondary-400 mb-1">Request</div><div className="whitespace-pre-wrap break-words rounded bg-white dark:bg-secondary-900 border border-secondary-200 dark:border-secondary-800 p-2 font-mono text-[11px]">{d.request_text || '—'}</div></div>
      <div className="text-xs"><div className="text-secondary-400 mb-1">Response {d.response_components.length ? `(+${d.response_components.join(', ')})` : ''}</div><div className="whitespace-pre-wrap break-words rounded bg-white dark:bg-secondary-900 border border-secondary-200 dark:border-secondary-800 p-2 font-mono text-[11px]">{d.response_text || '—'}</div></div>
      {d.error && <div className="text-xs text-red-600 dark:text-red-400">Error at <b>{d.error.step}</b>: {d.error.message}</div>}
      <div><div className="text-secondary-400 text-xs mb-1">Execution timeline ({d.spans.length} steps)</div>{d.spans.map((s, i) => <SpanRow key={i} s={s} />)}</div>
    </div>
  );
};

const RequestTracesPanel: React.FC = () => {
  const [days, setDays] = useState<1 | 7 | 30>(7);
  const [status, setStatus] = useState<'' | 'ok' | 'partial' | 'error'>('');
  const [email, setEmail] = useState('');
  const [rows, setRows] = useState<TraceRow[]>([]);
  const [scanned, setScanned] = useState(0);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [open, setOpen] = useState<string | null>(null);

  const load = useCallback(() => {
    setLoading(true); setErr('');
    getRequestTraces(days, email.trim() || undefined, status || undefined)
      .then((r) => { setRows(r.traces); setScanned(r.scanned); })
      .catch((e) => setErr(e?.message || 'Failed to load traces'))
      .finally(() => setLoading(false));
  }, [days, status, email]);
  useEffect(() => { load(); }, [days, status]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className={card}>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <div>
          <h2 className="font-semibold">Request traces</h2>
          <p className="text-xs text-secondary-500">How each turn was answered — retrieval phase, model, integrations, reply, errors.</p>
        </div>
        <div className="flex items-center gap-1">
          {([1, 7, 30] as const).map((d) => <Chip key={d} active={days === d} onClick={() => setDays(d)}>{d}d</Chip>)}
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-2 mb-3">
        {(['', 'ok', 'partial', 'error'] as const).map((s) => <Chip key={s} active={status === s} onClick={() => setStatus(s)}>{s || 'all'}</Chip>)}
        <div className="flex items-center gap-1 ml-auto">
          <input className={input} placeholder="filter by email" value={email} onChange={(e) => setEmail(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && load()} />
          <button onClick={load} className="p-2 rounded-lg hover:bg-secondary-100 dark:hover:bg-secondary-800"><Search className="w-4 h-4" /></button>
        </div>
      </div>
      {loading ? <div className="flex items-center gap-2 text-sm text-secondary-500 py-6"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
        : err ? <div className="text-sm text-red-600 py-4">{err}</div>
        : !rows.length ? <div className="text-sm text-secondary-500 py-6">No traces in this window.</div>
        : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-secondary-400 text-left text-xs uppercase tracking-wide">
                <tr><th className="py-1">When</th><th>User</th><th>Request</th><th>Agents</th><th>Retrieval</th><th>Tier</th><th>Status</th></tr>
              </thead>
              <tbody>
                {rows.map((t) => (
                  <React.Fragment key={t.id}>
                    <tr onClick={() => setOpen(open === t.id ? null : t.id)} className="border-t border-secondary-100 dark:border-secondary-800 cursor-pointer hover:bg-secondary-50 dark:hover:bg-secondary-800/40">
                      <td className="py-1.5 whitespace-nowrap text-xs text-secondary-500">{t.created_iso ? new Date(t.created_iso).toLocaleString() : '—'}</td>
                      <td className="text-xs">{t.email || t.uid || '—'}</td>
                      <td className="max-w-[22rem] truncate">{t.request_text || '—'}</td>
                      <td className="text-xs">{t.responders.map((r) => r.name).join(', ') || '—'}</td>
                      <td className="text-xs whitespace-nowrap">{t.retrieval_mode === 'hybrid' ? 'P2' : t.retrieval_mode === 'keyword' ? 'P1' : '—'}{t.retrieval_chunks ? ` · ${t.retrieval_chunks}` : ''}</td>
                      <td className="text-xs">{t.model_tiers.join(', ') || '—'}</td>
                      <td className={`text-xs font-medium ${statusColor(t.status)}`}>{t.status}</td>
                    </tr>
                    {open === t.id && <tr><td colSpan={7} className="p-0"><TraceDetailView id={t.id} /></td></tr>}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
            <div className="text-xs text-secondary-400 mt-2">{rows.length} shown · {scanned} scanned</div>
          </div>
        )}
    </div>
  );
};

export default RequestTracesPanel;
